import axios from 'axios';
import { OpenAPI } from '../api/core/OpenAPI';
import { store } from '../redux/store';

export interface SoldItemsFilterParams {
  page?: number;
  limit?: number;
  productCodeUnique?: string;
  categoryName?: string;
  saleDateFrom?: string;
  saleDateTo?: string;
  sellerId?: string;
  sellerSearch?: string;
  locationId?: number;
  hasCustomer?: boolean;
  customerSearch?: string;
  finalPriceMin?: number;
  finalPriceMax?: number;
  orderCode?: string;
  branchReceiptCode?: string;
  daysUntilSaleMin?: number;
  daysUntilSaleMax?: number;
}

export interface SoldItemListRow {
  salesOrderItemId: number;
  warehouseInventoryProductId: number;
  orderId: number;
  orderCode: string;
  branchReceiptCode?: string | null;
  productCodeUnique: string;
  categoryName: string;
  saleDate: string;
  sellerId: string;
  sellerDisplayName: string;
  locationId: number;
  locationName: string;
  hasCustomer: boolean;
  customerName?: string | null;
  finalPrice: number;
  hasInvoice: boolean;
  daysUntilSale: number;
}

export interface SoldItemsListResponse {
  data: SoldItemListRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SoldPieceHistoryItem {
  id: number;
  actionType: string;
  exchangeId?: string | null;
  returnId?: string | null;
  productName: string;
  warehouseInventoryProductId?: number | null;
  productCodeUnique?: string | null;
  originalPrice: number;
  finalPrice: number;
  discountType?: string | null;
  discountValue?: number | null;
  notes?: string | null;
  createdAt: string;
  processedByUsername?: string | null;
}

export interface SoldPiecePayment {
  id: number;
  paymentAmount: number;
  paymentDate: string;
  paymentMethod?: string | null;
  paymentMethodName?: string | null;
  status: string;
  bankReference?: string | null;
  paymentNotes?: string | null;
  processedByName?: string | null;
}

export interface SoldPieceDetail {
  summary: {
    salesOrderItemId: number;
    warehouseInventoryProductId: number;
    orderId: number;
    orderCode: string;
    branchReceiptCode?: string | null;
    productCodeUnique: string;
    productName: string;
    categoryName: string;
    saleDate: string;
    sellerDisplayName: string;
    locationName: string;
    hasCustomer: boolean;
    customerName?: string | null;
    finalPrice: number;
    hasInvoice: boolean;
  };
  piece: Record<string, unknown>;
  saleLine: {
    originalPrice: number;
    finalPrice: number;
    discountType: string;
    discountValue?: number | null;
    itemDiscountApplied: boolean;
  };
  order: {
    id: number;
    orderCode: string;
    status: string;
    saleDate: string;
    salesChannel: string;
    totalAmount: number;
    totalPaid: number;
    balanceDue: number;
    customerName?: string | null;
    locationName: string;
    sellerDisplayName: string;
    hasInvoice: boolean;
  };
  payments: SoldPiecePayment[];
  pieceHistory: SoldPieceHistoryItem[];
}

function getAuthHeaders(): Record<string, string> {
  const token = store.getState().auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildQueryParams(filters: SoldItemsFilterParams): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.append(key, String(value));
  });
  return params;
}

export const soldItemsService = {
  async list(filters: SoldItemsFilterParams): Promise<SoldItemsListResponse> {
    const params = buildQueryParams(filters);
    const response = await axios.get<SoldItemsListResponse>(
      `${OpenAPI.BASE}/sales-orders/sold-items?${params.toString()}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  async getDetail(salesOrderItemId: number): Promise<SoldPieceDetail> {
    const response = await axios.get<SoldPieceDetail>(
      `${OpenAPI.BASE}/sales-orders/sold-items/${salesOrderItemId}/detail`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  async exportCsv(filters: SoldItemsFilterParams): Promise<Blob> {
    const params = buildQueryParams({ ...filters, page: 1, limit: 50000 });
    const response = await axios.get(
      `${OpenAPI.BASE}/sales-orders/sold-items/export/csv?${params.toString()}`,
      { headers: getAuthHeaders(), responseType: 'blob' },
    );
    return response.data;
  },
};
